import { RawAxiosRequestHeaders } from 'axios';
import { OidcSettings } from '../interfaces/OidcSettings';
import { getWorker } from '../utils/getWorker';
import { DeferredAction } from '../types/DeferredAction';
import { getAuthCodes } from './getAuthCodes';
import { setAuthState } from './setAuthState';
import { WorkerCallBackMessage } from '../interfaces/WorkerCallBackMessage';
import { LOG, TERMINATE_TASK_ERROR, TERMINATE_TASK_SUCCESS } from '../types/WorkerCallBackActions';
import {
  WorkerActions,
  INIT_AUTH,
  AUTHENTICATE,
  GET_TOKEN,
  REFRESH_TOKEN,
  RELEASE_REQUEST,
  REVOKE_TOKEN,
  ADD_HEADERS,
  EVAL_CLAIM,
  GET_CLAIM,
  IS_TOKEN_EXPIRED,
} from '../types/WorkerActions';

export class WorkerServices {
  worker: Worker;
  tasks: Map<string, DeferredAction<unknown>>;
  log: ((level: string, obj: unknown) => void) | undefined = undefined;

  constructor(logFunc: (level: string, obj: unknown) => void) {
    this.tasks = new Map<string, DeferredAction<unknown>>();
    this.worker = getWorker();
    this.log = logFunc;
    this.worker.onmessage = ({ data: { id, data, error, action, log } }: MessageEvent<WorkerCallBackMessage>) => {
      switch (action) {
        case TERMINATE_TASK_ERROR:
          this.tasks.get(id)?.reject(error);
          this.tasks.delete(id);
          break;

        case TERMINATE_TASK_SUCCESS:
          this.tasks.get(id)?.resolve(data);
          this.tasks.delete(id);
          break;

        case LOG:
          logFunc(log.level, log.data);
          break;

        default:
          break;
      }
    };
  }

  private invokeAction<T>(action: WorkerActions, data: unknown = undefined): Promise<T> {
    const id = `${(Math.random() * 1000)}_${Date.now()}`;
    const deferred = new DeferredAction<T>();
    this.tasks.set(id, deferred as DeferredAction<unknown>);
    this.worker.postMessage({ action, id, data });
    return deferred.promise;
  }

  async init(config: OidcSettings): Promise<string> {
    const result = await this.invokeAction<{ endPointUrl: string; state: string; codeVerifier: string }>(INIT_AUTH, { config });
    setAuthState(result.state, result.codeVerifier);
    return Promise.resolve(result.endPointUrl);
  }

  authenticate = (config: OidcSettings): Promise<string> => this.invokeAction(AUTHENTICATE, { codes: getAuthCodes(), config });
  getToken = (): Promise<string> => this.invokeAction(GET_TOKEN);
  isTokenExpired = (): Promise<boolean> => this.invokeAction(IS_TOKEN_EXPIRED);
  refreshToken = (): Promise<string> => this.invokeAction(REFRESH_TOKEN);
  releaseRequest = (): Promise<void> => this.invokeAction(RELEASE_REQUEST);
  logOff = (): Promise<never> => this.invokeAction(REVOKE_TOKEN);
  addHeaders = (url: string, headers: RawAxiosRequestHeaders): Promise<RawAxiosRequestHeaders> => this.invokeAction(ADD_HEADERS, { url, headers });
  evaluateClaim = (claim: string, value?: string) => this.invokeAction<{ hasClaim: boolean }>(EVAL_CLAIM, { claim, value });
  getClaim = (claim: string) => this.invokeAction<{ claimValue: unknown }>(GET_CLAIM, claim);
}
